import { useEffect, useState } from "react";
import { ListBase, useListContext } from "ra-core";
import { Link } from "react-router";
import { Plus, Search } from "lucide-react";
import { ListPagination } from "@/components/admin/list-pagination";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { EmpresasTabs } from "../layout/RaftelLayout";
import { MobilePage } from "../MobilePage";
import { OfferDot, StagePill } from "../ui";
import { offerColor, useOffers } from "../useOffers";

type BoardRow = {
  id: number;
  name: string;
  city: string | null;
  sector: string | null;
  deals: { id: number; key: string | null; offer_id: number; stage: string }[];
  last_contact: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_title: string | null;
  contact_phone: string | null;
};

const ago = (iso: string | null) => {
  if (!iso) return "Nunca";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days <= 0 ? "Hoy" : days === 1 ? "Ayer" : `Hace ${days} días`;
};

/** Empresas estilo Monday (PRD D.4) sobre la vista company_board. */
export const CompanyBoard = () => {
  const isMobile = useIsMobile();
  return (
    <ListBase
      resource="company_board"
      perPage={50}
      sort={{ field: "name", order: "ASC" }}
    >
      <MobilePage title="Empresas">
        {isMobile && <EmpresasTabs />}
        <div className="flex flex-col gap-3">
          <Toolbar />
          {isMobile ? <Cards /> : <Table />}
          <ListPagination rowsPerPageOptions={[50, 100]} />
        </div>
      </MobilePage>
    </ListBase>
  );
};

const Toolbar = () => {
  const { filterValues, setFilters, total } = useListContext();
  const [q, setQ] = useState<string>(filterValues["name@ilike"] ?? "");
  useEffect(() => {
    const t = setTimeout(
      () => setFilters(q.trim() ? { "name@ilike": q.trim() } : {}),
      300,
    );
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex h-9 min-w-52 flex-1 items-center gap-2 rounded-lg border bg-card px-2.5 md:max-w-80">
        <Search className="size-4 text-muted-foreground" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar empresa…"
          className="w-full bg-transparent text-sm outline-none"
          aria-label="Buscar empresa"
        />
      </label>
      <span className="text-sm text-muted-foreground">
        {total ?? "…"} empresas
      </span>
      <Button asChild variant="outline" className="ml-auto">
        <Link to="/directory">
          <Plus /> Traer del directorio
        </Link>
      </Button>
    </div>
  );
};

const Products = ({ r }: { r: BoardRow }) => {
  const { data: offers } = useOffers();
  if (!r.deals.length)
    return <span className="text-sm text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {r.deals.map((d) => (
        <span key={d.id} className="inline-flex items-center gap-1.5">
          <OfferDot
            color={offerColor(offers?.find((o) => o.id === d.offer_id))}
          />
          <span className="font-mono text-xs">{d.key}</span>
          <StagePill stage={d.stage} />
        </span>
      ))}
    </div>
  );
};

const contactName = (r: BoardRow) =>
  [r.contact_first_name, r.contact_last_name].filter(Boolean).join(" ");
const EMPTY = "Sin empresas. Tráelas del Directorio con “Pasar al CRM”.";

const Table = () => {
  const { data, isPending } = useListContext<BoardRow>();
  if (isPending) return null;
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full min-w-[860px] text-sm">
        <thead>
          <tr className="border-b text-left text-xs uppercase tracking-wide text-[var(--faint)]">
            {[
              "Empresa",
              "Productos",
              "Último contacto",
              "Contacto principal",
            ].map((h) => (
              <th key={h} className="px-3 py-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.map((r) => (
            <tr
              key={r.id}
              className="border-b last:border-0 hover:bg-secondary/60"
            >
              <td className="px-3 py-2">
                <Link
                  to={`/companies/${r.id}/show`}
                  className="font-bold hover:text-primary hover:underline"
                >
                  {r.name}
                </Link>
                <div className="text-xs text-muted-foreground">
                  {[r.city, r.sector].filter(Boolean).join(" · ")}
                </div>
              </td>
              <td className="px-3 py-2">
                <Products r={r} />
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                {ago(r.last_contact)}
              </td>
              <td className="px-3 py-2">
                {contactName(r) || "—"}
                <div className="text-xs text-muted-foreground">
                  {[r.contact_title, r.contact_phone]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data?.length && (
        <p className="p-6 text-center text-muted-foreground">{EMPTY}</p>
      )}
    </div>
  );
};

const Cards = () => {
  const { data, isPending } = useListContext<BoardRow>();
  if (isPending) return null;
  if (!data?.length)
    return <p className="py-6 text-muted-foreground">{EMPTY}</p>;
  return (
    <ul className="flex flex-col gap-2">
      {data.map((r) => (
        <li key={r.id}>
          <Link
            to={`/companies/${r.id}/show`}
            className="flex flex-col gap-1.5 rounded-xl border bg-card p-3"
          >
            <span className="flex items-baseline justify-between gap-2">
              <b className="truncate">{r.name}</b>
              <span className="shrink-0 text-xs text-muted-foreground">
                {ago(r.last_contact)}
              </span>
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {[contactName(r), r.contact_title, r.city]
                .filter(Boolean)
                .join(" · ")}
            </span>
            <Products r={r} />
          </Link>
        </li>
      ))}
    </ul>
  );
};
